import React, { ReactElement, useMemo, useState } from 'react';

import CustomIcon from '@components/utils/CustomIcon/index';
import { useDevice } from '@contexts/device';
import Typography from '@components/Typography';

import {
  StyledTab,
  StyledTabs,
  TabDateText,
  TabMainDiv,
  TabText,
} from './styled';
import { TabItemProps, TabProps } from './props';

export const TabItem = ({
  isSelected,
  text,
  icon,
  onClick,
  disabled,
  control,
  startDate,
  endDate,
}: TabItemProps) => {
  const [hover, setHover] = useState(false);
  const { isDesktop } = useDevice();

  const formatDateToTurkish = (dateString) => {
    const date = new Date(dateString);
    const formatter = new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    return formatter.format(date);
  };

  const seasonDate = useMemo(() => {
    if (startDate && endDate) {
      return `${formatDateToTurkish(startDate)} - ${formatDateToTurkish(
        endDate
      )}`;
    }
  }, [startDate, endDate]);

  return (
    <StyledTab
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      selected={isSelected}
      hover={hover}
      onClick={onClick}
      disabled={disabled}
      control={control}
    >
      <div>
        <TabMainDiv>
          <CustomIcon
            paletteColor={isSelected ? 'white' : 'D300'}
            fontSize={isDesktop ? 24 : 18}
            width={isDesktop ? 24 : 18}
            height={isDesktop ? 24 : 18}
            icon={icon}
          />
          <TabText isSelected={isSelected}>
            <Typography
              variant='caption'
              weight={isSelected ? 'Bold' : 'Regular'}
            >
              {text === 'Summer'
                ? 'Yaz Sezonu'
                : text === 'Winter'
                ? 'Kış Sezonu'
                : null}
            </Typography>
          </TabText>
        </TabMainDiv>
        <TabDateText isSelected={isSelected}>
          <Typography
            variant={isDesktop ? 'caption1' : 'caption3'}
            weight='Regular'
          >
            {seasonDate}
          </Typography>
        </TabDateText>
      </div>
    </StyledTab>
  );
};

const Tab = ({
  onTabSelection,
  children,
  selected,
}: React.PropsWithChildren<TabProps>) => {
  const childCount = React.Children.count(children);
  return (
    <StyledTabs isTabCount={childCount > 1 ? true : false}>
      {React.Children.map(children, (child, index) => {
        const childElement = child as React.ReactElement;
        const childProps = childElement.props;
        return React.cloneElement(child as ReactElement, {
          onClick: () => onTabSelection && onTabSelection(childProps.text),
          isSelected: selected === childProps.text,
          key: index,
        });
      })}
    </StyledTabs>
  );
};

export default Tab;
